// PWA plumbing: service worker registration, update detection and the
// install prompt. Kept out of components so App stays declarative.

const UPDATE_CHECK_MS = 30 * 60 * 1000;

let waitingWorker = null;
let reloading = false;

/** True while a Clock In / Clock Out request is in flight. */
export const clockBusy = {
    value: false,
    set(v) { this.value = v; },
};

export function registerServiceWorker({ onUpdateReady }) {
    // Secure context only (https or localhost) — that is all a service worker
    // needs. Registered in every environment so install + update behaviour can
    // be validated on preview as well as production.
    const secure = window.isSecureContext || window.location.hostname === "localhost";
    if (!("serviceWorker" in navigator) || !secure) return;

    window.addEventListener("load", async () => {
        try {
            const reg = await navigator.serviceWorker.register("/sw.js");
            // Only an *update* that takes over an already-controlled page needs a
            // reload. On the very first install there is no controller, so
            // reloading would throw the user out of whatever they just did.
            const hadController = Boolean(navigator.serviceWorker.controller);

            const track = (worker) => {
                if (!worker) return;
                worker.addEventListener("statechange", () => {
                    if (worker.state === "installed" && navigator.serviceWorker.controller) {
                        waitingWorker = worker;
                        onUpdateReady();
                    }
                });
            };

            if (reg.waiting && navigator.serviceWorker.controller) {
                waitingWorker = reg.waiting;
                onUpdateReady();
            }
            reg.addEventListener("updatefound", () => track(reg.installing));

            // Check for a newer build periodically and when the app regains focus,
            // so installed users never sit on a stale JS bundle.
            setInterval(() => reg.update().catch(() => null), UPDATE_CHECK_MS);
            document.addEventListener("visibilitychange", () => {
                if (document.visibilityState === "visible") reg.update().catch(() => null);
            });

            navigator.serviceWorker.addEventListener("controllerchange", () => {
                if (reloading || !hadController) return;
                reloading = true;
                window.location.reload();
            });
        } catch (err) {
            // A failed registration must never break the portal.
            console.debug("[pwa] service worker registration skipped:", err?.message || err);
        }
    });
}

/** Activate the new version. Never called automatically. */
export function applyUpdate() {
    if (waitingWorker) {
        waitingWorker.postMessage({ type: "SKIP_WAITING" });
        return;
    }
    window.location.reload();
}

export function isStandalone() {
    return (
        window.matchMedia?.("(display-mode: standalone)").matches ||
        window.matchMedia?.("(display-mode: minimal-ui)").matches ||
        window.navigator.standalone === true
    );
}
